/**
 * AxLabs Experiments - RC Circuit (Charging/Discharging)
 *
 * Demonstrates capacitor charging and discharging through a resistor.
 *
 * Governing equations:
 * - Charging: V_c(t) = V_0(1 - e^(-t/τ))
 * - Discharging: V_c(t) = V_0 * e^(-t/τ)
 * - Time constant: τ = RC
 */
import { BaseSimulationEngine, DIMENSIONS, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'sourceVoltage',
        name: 'Source Voltage',
        symbol: 'V_0',
        unit: 'V',
        dimension: DIMENSIONS.ELECTRIC_POTENTIAL,
        min: 0,
        max: 24,
        default: 12,
        step: 0.5,
        description: 'EMF of the voltage source',
    },
    {
        id: 'resistance',
        name: 'Resistance',
        symbol: 'R',
        unit: 'Ω',
        dimension: DIMENSIONS.ELECTRIC_RESISTANCE,
        min: 100,
        max: 100000,
        default: 10000,
        step: 100,
        description: 'Resistance in the circuit',
    },
    {
        id: 'capacitance',
        name: 'Capacitance',
        symbol: 'C',
        unit: 'μF',
        dimension: DIMENSIONS.CAPACITANCE,
        min: 0.1,
        max: 1000,
        default: 100,
        step: 1,
        description: 'Capacitance (in microfarads)',
    },
    {
        id: 'initialCharge',
        name: 'Initial Charge Fraction',
        symbol: 'q_0/q_{max}',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 1,
        default: 0,
        step: 0.1,
        description: 'Initial charge as fraction of maximum (0 = uncharged, 1 = fully charged)',
    },
    {
        id: 'mode',
        name: 'Circuit Mode',
        symbol: '',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 1,
        default: 0,
        step: 1,
        description: '0 = Charging, 1 = Discharging',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'time_constant',
        name: 'Time Constant',
        latex: '\\tau = RC',
        variables: ['resistance', 'capacitance'],
        description: 'Characteristic time for charging/discharging',
    },
    {
        id: 'charging_voltage',
        name: 'Charging Voltage',
        latex: 'V_C(t) = V_0\\left(1 - e^{-t/\\tau}\\right)',
        variables: ['capacitorVoltage', 'sourceVoltage'],
        description: 'Capacitor voltage during charging',
        condition: (params) => (params['mode'] ?? 0) === 0,
    },
    {
        id: 'discharging_voltage',
        name: 'Discharging Voltage',
        latex: 'V_C(t) = V_0 \\cdot e^{-t/\\tau}',
        variables: ['capacitorVoltage', 'sourceVoltage'],
        description: 'Capacitor voltage during discharging',
        condition: (params) => (params['mode'] ?? 0) === 1,
    },
    {
        id: 'current',
        name: 'Circuit Current',
        latex: 'I = \\frac{V_0 - V_C}{R} = \\frac{V_0}{R}e^{-t/\\tau}',
        variables: ['current', 'sourceVoltage', 'capacitorVoltage', 'resistance'],
        description: 'Current decays exponentially',
    },
    {
        id: 'charge',
        name: 'Capacitor Charge',
        latex: 'Q = CV_C',
        variables: ['charge', 'capacitance', 'capacitorVoltage'],
        description: 'Charge stored on capacitor',
    },
    {
        id: 'stored_energy',
        name: 'Stored Energy',
        latex: 'U = \\frac{1}{2}CV_C^2 = \\frac{Q^2}{2C}',
        variables: ['storedEnergy', 'capacitance', 'capacitorVoltage'],
        description: 'Energy stored in electric field',
    },
    {
        id: 'power_dissipation',
        name: 'Power Dissipation',
        latex: 'P = I^2 R = \\frac{(V_0 - V_C)^2}{R}',
        variables: ['current', 'resistance'],
        description: 'Power dissipated in resistor',
    },
    {
        id: 'kvl',
        name: "Kirchhoff's Voltage Law",
        latex: 'V_0 = V_R + V_C = IR + \\frac{Q}{C}',
        variables: ['sourceVoltage', 'resistorVoltage', 'capacitorVoltage'],
        description: 'Sum of voltages around loop equals zero',
        condition: (params) => (params['mode'] ?? 0) === 0,
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'fully_charged',
        name: 'Fully Charged',
        description: 'Capacitor has reached maximum charge',
        check: (state, params) => {
            const V0 = params['sourceVoltage'] ?? 12;
            return (state.variables['capacitorVoltage'] ?? 0) > 0.99 * V0;
        },
        severity: 'warning',
        educationalMessage: 'The capacitor is fully charged (>99% of source voltage). ' +
            'Current has effectively stopped flowing.',
    },
    {
        id: 'fully_discharged',
        name: 'Fully Discharged',
        description: 'Capacitor has discharged completely',
        check: (state, params) => {
            const mode = params['mode'] ?? 0;
            return mode === 1 && (state.variables['capacitorVoltage'] ?? 1) < 0.01;
        },
        severity: 'warning',
        educationalMessage: 'The capacitor has fully discharged. ' +
            'All stored energy has been dissipated in the resistor.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const RC_CIRCUIT_CONFIG = {
    id: 'rc-circuit',
    slug: 'rc-circuit',
    name: 'RC Circuit (Charging/Discharging)',
    domain: 'physics',
    description: 'Explore capacitor charging and discharging',
    longDescription: `
An RC circuit consists of a resistor and capacitor in series.
The time constant τ = RC determines how quickly the circuit responds.

Charging:
- Capacitor voltage rises exponentially toward source voltage
- Current starts high and decays exponentially
- At t = τ, voltage reaches 63.2% of final value
- At t = 5τ, considered fully charged (>99%)

Discharging:
- Capacitor voltage decays exponentially
- At t = τ, voltage drops to 36.8% of initial
- At t = 5τ, considered fully discharged

Energy analysis:
- Half of source energy is dissipated in resistor during charging
- Stored energy U = ½CV² is dissipated during discharge
  `.trim(),
    version: 1,
    conceptTags: ['circuits', 'capacitor', 'exponential-decay', 'time-constant', 'energy'],
    difficulty: 4,
    prerequisites: ['ohms-law', 'capacitance'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'capacitorVoltage', type: 'computed', compute: (p) => {
                const V0 = p['sourceVoltage'] ?? 12;
                const q0 = p['initialCharge'] ?? 0;
                const mode = p['mode'] ?? 0;
                return mode === 0 ? q0 * V0 : V0;
            } },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.0001, // Need small dt for fast circuits
    maxDt: 0.001,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class RCCircuitSimulation extends BaseSimulationEngine {
    config = RC_CIRCUIT_CONFIG;
    /** Accumulated energy dissipated */
    totalDissipated = 0;
    constructor() {
        super({ capacitorVoltage: 0, current: 0, resistorVoltage: 0, charge: 0, storedEnergy: 0, dissipatedEnergy: 0 }, { type: 'euler', dt: 0.0001, maxSubsteps: 20 });
    }
    getInitialState(params) {
        const V0 = params['sourceVoltage'] ?? 12;
        const R = params['resistance'] ?? 10000;
        const C_uF = params['capacitance'] ?? 100;
        const C = C_uF * 1e-6; // Convert to Farads
        const q0Frac = params['initialCharge'] ?? 0;
        const mode = params['mode'] ?? 0;
        // Initial voltage
        const Vc = mode === 0 ? q0Frac * V0 : V0 * (1 - q0Frac + q0Frac); // For discharging, start at V0
        const VcActual = mode === 1 ? V0 : Vc;
        // Initial current
        const Vr = mode === 0 ? V0 - VcActual : VcActual; // Resistor voltage
        const I = mode === 0 ? Vr / R : VcActual / R;
        this.totalDissipated = 0;
        return {
            capacitorVoltage: VcActual,
            current: mode === 0 ? I : -I, // Negative current for discharge
            resistorVoltage: Math.abs(Vr),
            charge: C * VcActual,
            storedEnergy: 0.5 * C * VcActual * VcActual,
            dissipatedEnergy: 0,
        };
    }
    /**
     * Get time constant τ = RC
     */
    getTimeConstant() {
        const R = this.parameters['resistance'] ?? 10000;
        const C_uF = this.parameters['capacitance'] ?? 100;
        const C = C_uF * 1e-6;
        return R * C;
    }
    computeDerivatives(state, params) {
        const V0 = params['sourceVoltage'] ?? 12;
        const R = params['resistance'] ?? 10000;
        const C_uF = params['capacitance'] ?? 100;
        const C = C_uF * 1e-6;
        const mode = params['mode'] ?? 0;
        let dVc;
        if (mode === 0) {
            // Charging: dVc/dt = (V0 - Vc) / (RC)
            dVc = (V0 - state.capacitorVoltage) / (R * C);
        }
        else {
            // Discharging: dVc/dt = -Vc / (RC)
            dVc = -state.capacitorVoltage / (R * C);
        }
        return {
            capacitorVoltage: dVc,
        };
    }
    integrate(dt) {
        const params = this.parameters;
        const V0 = params['sourceVoltage'] ?? 12;
        const R = params['resistance'] ?? 10000;
        const C_uF = params['capacitance'] ?? 100;
        const C = C_uF * 1e-6;
        const mode = params['mode'] ?? 0;
        // Use analytical solution for stability
        const tau = R * C;
        const Vc0 = this.state.capacitorVoltage;
        let newVc;
        if (mode === 0) {
            // Charging: Vc(t+dt) = V0 + (Vc0 - V0) * e^(-dt/τ)
            newVc = V0 + (Vc0 - V0) * Math.exp(-dt / tau);
        }
        else {
            // Discharging: Vc(t+dt) = Vc0 * e^(-dt/τ)
            newVc = Vc0 * Math.exp(-dt / tau);
        }
        // Current
        let I;
        if (mode === 0) {
            I = (V0 - newVc) / R;
        }
        else {
            I = -newVc / R; // Negative for discharge
        }
        // Energy dissipated this step: P * dt = I² * R * dt
        const powerDissipated = I * I * R;
        this.totalDissipated += powerDissipated * dt;
        // Update state
        this.state.capacitorVoltage = Math.max(0, newVc);
        this.state.current = I;
        this.state.resistorVoltage = Math.abs(I * R);
        this.state.charge = C * newVc;
        this.state.storedEnergy = 0.5 * C * newVc * newVc;
        this.state.dissipatedEnergy = this.totalDissipated;
    }
    computeEnergy(state, params) {
        const C_uF = params['capacitance'] ?? 100;
        const C = C_uF * 1e-6;
        return {
            kinetic: 0, // No kinetic energy in this context
            potential: 0.5 * C * state.capacitorVoltage * state.capacitorVoltage,
            thermal: this.totalDissipated,
            total: 0.5 * C * state.capacitorVoltage * state.capacitorVoltage + this.totalDissipated,
        };
    }
    getPositionForStabilityCheck() {
        return this.state.capacitorVoltage;
    }
    /**
     * Get analytical solution for comparison
     */
    getAnalyticalVoltage(t) {
        const V0 = this.parameters['sourceVoltage'] ?? 12;
        const tau = this.getTimeConstant();
        const mode = this.parameters['mode'] ?? 0;
        const q0 = this.parameters['initialCharge'] ?? 0;
        if (mode === 0) {
            // Charging from q0 fraction
            const Vc0 = q0 * V0;
            return V0 + (Vc0 - V0) * Math.exp(-t / tau);
        }
        else {
            // Discharging from V0
            return V0 * Math.exp(-t / tau);
        }
    }
}
/**
 * Factory function
 */
export function createRCCircuit() {
    return new RCCircuitSimulation();
}
//# sourceMappingURL=rc-circuit.js.map